import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { toast } from '@/store/toastStore';
import {
  createKbQuiz,
  kbQueryKeys,
  listKbChapters,
  listKbQuizzes,
  listKnowledgeBaseDocuments,
  type KbQuiz,
} from '@/services/knowledge-base.service';

export function QuizzesListPage() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const [title, setTitle] = useState('');
  const [targetType, setTargetType] = useState<'document' | 'chapter'>('document');
  const [targetId, setTargetId] = useState(0);
  const [docSearch, setDocSearch] = useState('');
  const [passThreshold, setPassThreshold] = useState(80);

  const quizzesQuery = useQuery({
    queryKey: kbQueryKeys.quizzes(),
    queryFn: () => listKbQuizzes(),
  });

  const docsQuery = useQuery({
    queryKey: kbQueryKeys.documents({ q: docSearch.trim() || undefined, perPage: 25 }),
    queryFn: () => listKnowledgeBaseDocuments({ q: docSearch.trim() || undefined, perPage: 25 }),
    enabled: showCreate && targetType === 'document',
  });

  const chaptersListQuery = useQuery({
    queryKey: kbQueryKeys.chapters(),
    queryFn: () => listKbChapters(),
    enabled: showCreate && targetType === 'chapter',
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createKbQuiz({
        targetType,
        targetId,
        title: title.trim(),
        passThreshold,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.quizzes() });
      toast.success('Quiz created');
      setShowCreate(false);
      setTitle('');
      setTargetId(0);
      setPassThreshold(80);
    },
    onError: () => toast.error('Failed to create quiz'),
  });

  function handleCreate() {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!targetId) {
      toast.error('Pick a document or chapter');
      return;
    }
    createMutation.mutate();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
          New Quiz
        </Button>
      </div>

      {quizzesQuery.isLoading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading quizzes...
        </div>
      ) : !quizzesQuery.data?.length ? (
        <EmptyState
          icon={<ClipboardList className="h-8 w-8" />}
          title="No quizzes yet"
          description="Create a quiz on a document or chapter to require staff to pass before they can mark training complete."
          action={{ label: 'New Quiz', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Title</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Target</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Pass</th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {quizzesQuery.data.map((q: KbQuiz) => (
                <tr key={q.kbQuizId} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <Link to={`${q.kbQuizId}`} className="text-sm font-medium text-blue-600 hover:underline">
                      {q.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-700">
                    <span className="text-xs text-slate-500">{q.targetType}:</span>{' '}
                    {q.targetTitle || `#${q.targetId}`}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-700">{q.passThreshold}%</td>
                  <td className="px-4 py-2.5">
                    <Badge variant={q.status === 'active' ? 'success' : 'neutral'}>{q.status}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Quiz"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={createMutation.isPending}>
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Quiz title" required value={title} onChange={(e) => setTitle(e.target.value)} />

          <FormField label="Quiz attaches to">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setTargetType('document');
                  setTargetId(0);
                }}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  targetType === 'document'
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Document
              </button>
              <button
                type="button"
                onClick={() => {
                  setTargetType('chapter');
                  setTargetId(0);
                }}
                className={`flex-1 rounded-md border px-3 py-2 text-sm ${
                  targetType === 'chapter'
                    ? 'border-blue-500 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Chapter
              </button>
            </div>
          </FormField>

          {targetType === 'document' ? (
            <>
              <Input
                placeholder="Search documents..."
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
              />
              <div className="max-h-[180px] overflow-y-auto rounded-md border border-slate-200">
                {docsQuery.data?.data.map((d) => (
                  <button
                    key={d.kbDocumentId}
                    type="button"
                    onClick={() => setTargetId(d.kbDocumentId)}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                      targetId === d.kbDocumentId ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="truncate">{d.title}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="max-h-[180px] overflow-y-auto rounded-md border border-slate-200">
              {chaptersListQuery.data?.map((c) => (
                <button
                  key={c.kbChapterId}
                  type="button"
                  onClick={() => setTargetId(c.kbChapterId)}
                  className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                    targetId === c.kbChapterId ? 'bg-blue-50 text-blue-700' : 'hover:bg-slate-50'
                  }`}
                >
                  <span className="truncate">{c.title}</span>
                </button>
              ))}
            </div>
          )}

          <FormField label={`Pass threshold (${passThreshold}%)`}>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={passThreshold}
              onChange={(e) => setPassThreshold(Number(e.target.value))}
              className="w-full"
            />
          </FormField>
        </div>
      </Modal>
    </div>
  );
}
